
import { EventSpeaker } from "@/types/EventSpeaker";
import { Filter } from "@/types/Filter";
import { ConditionGroup } from "@/types/EventRule";
import {
  listEventSpeakersPaginated,
  countEventSpeakers as countEventSpeakersApi,
  exportEventSpeakers as exportEventSpeakersApi,
  promoteEventSpeaker,
  type PromoteSpeakerResult,
  linkSpeakerSponsor,
  unlinkSpeakerSponsor,
} from "@/lib/api/clients/EventSpeakerClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";

/**
 * Wave A speaker hub list — POST /event-speakers/{eventId}/list. Page is
 * 0-based (guest/check-in convention); pageSize capped at 100 (mirrors the
 * resource-level cap so a bad caller fails fast client-side).
 */
export async function getEventSpeakersList(
  eventId: string,
  searchTerm: string,
  page: number,
  pageSize: number,
  filters: Filter[],
  sortBy: string = "sortIndex",
  sortOrder: "asc" | "desc" = "asc",
  conditions?: ConditionGroup,
): Promise<EventSpeaker[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (pageSize < 1 || pageSize > 100) throw new Error("validationError");
    if (page < 0) throw new Error("validationError");

    const response = await listEventSpeakersPaginated(eventId, {
      searchTerm,
      page,
      pageSize,
      filters,
      sortBy,
      sortOrder,
      ...(conditions ? { conditions } : {}),
    });

    if (!Array.isArray(response)) throw new Error("unknown");

    return response;
  } catch (error) {
    console.error("Error in getEventSpeakersList:", error);
    return handleErrorServer(error);
  }
}

export async function getEventSpeakersCount(
  eventId: string,
  filters: Filter[] = [],
  searchTerm: string = "",
  conditions?: ConditionGroup,
): Promise<number | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await countEventSpeakersApi(eventId, {
      searchTerm,
      filters,
      ...(conditions ? { conditions } : {}),
    });
  } catch (error) {
    console.error("Error in getEventSpeakersCount:", error);
    return handleErrorServer(error);
  }
}

/**
 * Wave B — promote a speaker to an event guest. Explicit-guestId-first flow
 * (2026-07-23): `opts.attachGuestId` links that exact guest (wins over
 * `createNew`); `opts.createNew` mints a new guest without email matching;
 * neither → legacy server-side email dedupe. On the multi-match 409 the
 * returned {@link ServerError} carries `message === "multipleGuestMatches"`
 * AND `candidates` (the picker rows `{guestId, firstName, lastName, email}`);
 * the caller retries with the chosen `attachGuestId`. Other conflict codes
 * (`guestAlreadyLinked`, `eventAtCapacity`) arrive as plain ServerErrors for
 * the errors-namespace toast.
 */
export async function promoteSpeaker(
  eventId: string,
  speakerId: string,
  opts?: { attachGuestId?: string; createNew?: boolean },
): Promise<PromoteSpeakerResult | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(speakerId))
      throw new Error("validationError");
    if (
      opts?.attachGuestId !== undefined &&
      !isValidObjectId(opts.attachGuestId)
    )
      throw new Error("validationError");

    return await promoteEventSpeaker(eventId, speakerId, opts);
  } catch (error) {
    console.error("Error in promoteSpeaker:", error);
    return handleErrorServer(error);
  }
}

/** Wave L: link/unlink the speaker's sponsor org (overwrite allowed). */
export async function linkSpeakerToSponsor(
  eventId: string,
  speakerId: string,
  sponsorId: string,
): Promise<void | ServerError> {
  try {
    if (
      !isValidObjectId(eventId) ||
      !isValidObjectId(speakerId) ||
      !isValidObjectId(sponsorId)
    )
      throw new Error("validationError");
    return await linkSpeakerSponsor(eventId, speakerId, sponsorId);
  } catch (error) {
    console.error("Error in linkSpeakerToSponsor:", error);
    return handleErrorServer(error);
  }
}

export async function unlinkSpeakerFromSponsor(
  eventId: string,
  speakerId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(speakerId))
      throw new Error("validationError");
    return await unlinkSpeakerSponsor(eventId, speakerId);
  } catch (error) {
    console.error("Error in unlinkSpeakerFromSponsor:", error);
    return handleErrorServer(error);
  }
}

/** Synchronous export (csv|excel) — filter/search-scoped per the contract. */
export async function exportEventSpeakers(
  eventId: string,
  params: {
    format: "csv" | "excel";
    fields?: string;
    searchTerm?: string;
    filters?: Filter[];
    conditions?: ConditionGroup;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    columnLabels?: Record<string, string>;
  },
): Promise<Blob | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await exportEventSpeakersApi(eventId, params);
  } catch (error) {
    console.error("Error in exportEventSpeakers:", error);
    return handleErrorServer(error);
  }
}
